import { needsBody, partsHaveVars } from './requestParts';

// ─── Go ────────────────────────────────────────────────────────────────────

export function goBacktickLiteral(str) {
  if (!str.includes('`')) return `\`${str}\``;
  return str.split('`').map((part) => `\`${part}\``).join(' + "`" + ');
}

export const DEFAULT_GO_OPTIONS = {
  timeout: 0,
};

export function generateGo(parts, options = {}) {
  const { timeout = DEFAULT_GO_OPTIONS.timeout } = options;

  const lines = [];
  if (partsHaveVars(parts)) lines.push('// Replace {{...}} placeholders with actual values\n');

  const headerEntries = Object.entries(parts.headers);
  const withBody = needsBody(parts.method, parts.body);
  const imports = ['fmt', 'io', 'net/http'];
  if (withBody) imports.push('strings');
  if (timeout > 0) imports.push('time');

  lines.push('package main\n');
  lines.push('import (');
  for (const imp of [...imports].sort()) lines.push(`\t"${imp}"`);
  lines.push(')\n');

  lines.push('func main() {');
  lines.push(`\turl := "${parts.url}"\n`);

  if (withBody) {
    lines.push(`\tpayload := strings.NewReader(${goBacktickLiteral(parts.body)})\n`);
    lines.push(`\treq, err := http.NewRequest("${parts.method}", url, payload)`);
  } else {
    lines.push(`\treq, err := http.NewRequest("${parts.method}", url, nil)`);
  }

  lines.push('\tif err != nil {');
  lines.push('\t\tpanic(err)');
  lines.push('\t}\n');

  for (const [k, v] of headerEntries) {
    lines.push(`\treq.Header.Add("${k}", "${v.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}")`);
  }
  if (headerEntries.length) lines.push('');

  if (timeout > 0) {
    lines.push(`\tclient := &http.Client{Timeout: ${timeout} * time.Second}`);
  } else {
    lines.push('\tclient := &http.Client{}');
  }
  lines.push('\tres, err := client.Do(req)');
  lines.push('\tif err != nil {');
  lines.push('\t\tpanic(err)');
  lines.push('\t}');
  lines.push('\tdefer res.Body.Close()\n');
  lines.push('\tbody, err := io.ReadAll(res.Body)');
  lines.push('\tif err != nil {');
  lines.push('\t\tpanic(err)');
  lines.push('\t}\n');
  lines.push('\tfmt.Println(res.Status)');
  lines.push('\tfmt.Println(string(body))');
  lines.push('}');

  return lines.join('\n');
}

// ─── Java ──────────────────────────────────────────────────────────────────

export function javaEscape(s) {
  return String(s)
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r')
    .replace(/\t/g, '\\t');
}

export const DEFAULT_JAVA_OPTIONS = {
  timeout: 0,
  async:   false,
};

export function generateJava(parts, options = {}) {
  const {
    timeout = DEFAULT_JAVA_OPTIONS.timeout,
    async   = DEFAULT_JAVA_OPTIONS.async,
  } = options;

  const lines = [];
  if (partsHaveVars(parts)) lines.push('// Replace {{...}} placeholders with actual values\n');

  const headerEntries = Object.entries(parts.headers);
  const withBody = needsBody(parts.method, parts.body);

  lines.push('import java.net.URI;');
  lines.push('import java.net.http.HttpClient;');
  lines.push('import java.net.http.HttpRequest;');
  lines.push('import java.net.http.HttpResponse;');
  if (timeout > 0) lines.push('import java.time.Duration;');
  lines.push('');
  lines.push('public class Main {');
  lines.push(`    public static void main(String[] args) throws Exception {`);

  if (timeout > 0) {
    lines.push(`        HttpClient client = HttpClient.newBuilder()`);
    lines.push(`            .connectTimeout(Duration.ofSeconds(${timeout}))`);
    lines.push('            .build();\n');
  } else {
    lines.push('        HttpClient client = HttpClient.newHttpClient();\n');
  }

  lines.push('        HttpRequest request = HttpRequest.newBuilder()');
  lines.push(`            .uri(URI.create("${javaEscape(parts.url)}"))`);

  if (timeout > 0) {
    lines.push(`            .timeout(Duration.ofSeconds(${timeout}))`);
  }

  for (const [k, v] of headerEntries) {
    lines.push(`            .header("${javaEscape(k)}", "${javaEscape(v)}")`);
  }

  const m = parts.method;
  if (withBody) {
    const body = `"${javaEscape(parts.body)}"`;
    if (m === 'POST') {
      lines.push(`            .POST(HttpRequest.BodyPublishers.ofString(${body}))`);
    } else {
      lines.push(`            .method("${m}", HttpRequest.BodyPublishers.ofString(${body}))`);
    }
  } else if (m === 'GET') {
    lines.push('            .GET()');
  } else if (m === 'DELETE') {
    lines.push('            .DELETE()');
  } else {
    lines.push(`            .method("${m}", HttpRequest.BodyPublishers.noBody())`);
  }

  lines.push('            .build();\n');

  if (async) {
    lines.push('        client.sendAsync(request, HttpResponse.BodyHandlers.ofString())');
    lines.push('            .thenApply(response -> {');
    lines.push('                System.out.println(response.statusCode());');
    lines.push('                System.out.println(response.body());');
    lines.push('                return response;');
    lines.push('            })');
    lines.push('            .join();');
  } else {
    lines.push('        HttpResponse<String> response = client.send(');
    lines.push('            request, HttpResponse.BodyHandlers.ofString()');
    lines.push('        );\n');
    lines.push('        System.out.println(response.statusCode());');
    lines.push('        System.out.println(response.body());');
  }

  lines.push('    }');
  lines.push('}');

  return lines.join('\n');
}

// ─── C# ────────────────────────────────────────────────────────────────────

export function csEscape(s) {
  return String(s)
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r');
}

export const DEFAULT_CSHARP_OPTIONS = {
  timeout:    0,
  topLevel:   false,
};

export function generateCSharp(parts, options = {}) {
  const {
    timeout  = DEFAULT_CSHARP_OPTIONS.timeout,
    topLevel = DEFAULT_CSHARP_OPTIONS.topLevel,
  } = options;

  const lines = [];
  if (partsHaveVars(parts)) lines.push('// Replace {{...}} placeholders with actual values\n');

  const headerEntries = Object.entries(parts.headers);
  const withBody = needsBody(parts.method, parts.body);
  const ctEntry = headerEntries.find(([k]) => k.toLowerCase() === 'content-type');
  const otherHeaders = headerEntries.filter(([k]) => k.toLowerCase() !== 'content-type');

  const indent = topLevel ? '' : '        ';
  const i1     = topLevel ? '' : '    ';

  lines.push('using System.Net.Http;');
  if (withBody) lines.push('using System.Text;');
  if (!topLevel) {
    lines.push('using System.Threading.Tasks;\n');
    lines.push('class Program');
    lines.push('{');
    lines.push(`${i1}static async Task Main()`);
    lines.push(`${i1}{`);
  } else {
    lines.push('');
  }

  lines.push(`${indent}using var client = new HttpClient();`);

  if (timeout > 0) {
    lines.push(`${indent}client.Timeout = TimeSpan.FromSeconds(${timeout});`);
  }

  if (otherHeaders.length) lines.push('');
  for (const [k, v] of otherHeaders) {
    lines.push(`${indent}client.DefaultRequestHeaders.Add("${csEscape(k)}", "${csEscape(v)}");`);
  }
  if (otherHeaders.length) lines.push('');

  if (withBody) {
    const mime = ctEntry ? ctEntry[1].split(';')[0].trim() : 'text/plain';
    lines.push(`${indent}var content = new StringContent(`);
    lines.push(`${indent}    "${csEscape(parts.body)}",`);
    lines.push(`${indent}    Encoding.UTF8,`);
    lines.push(`${indent}    "${mime}"`);
    lines.push(`${indent});\n`);
  }

  const m = parts.method;
  const urlArg = `"${csEscape(parts.url)}"`;
  const contentArg = withBody ? 'content' : 'null';
  if (m === 'GET') {
    lines.push(`${indent}var response = await client.GetAsync(${urlArg});`);
  } else if (m === 'POST') {
    lines.push(`${indent}var response = await client.PostAsync(${urlArg}, ${contentArg});`);
  } else if (m === 'PUT') {
    lines.push(`${indent}var response = await client.PutAsync(${urlArg}, ${contentArg});`);
  } else if (m === 'DELETE') {
    lines.push(`${indent}var response = await client.DeleteAsync(${urlArg});`);
  } else {
    lines.push(`${indent}var req = new HttpRequestMessage(new HttpMethod("${m}"), ${urlArg});`);
    if (withBody) lines.push(`${indent}req.Content = content;`);
    lines.push(`${indent}var response = await client.SendAsync(req);`);
  }

  lines.push('');
  lines.push(`${indent}Console.WriteLine((int)response.StatusCode);`);
  lines.push(`${indent}Console.WriteLine(await response.Content.ReadAsStringAsync());`);

  if (!topLevel) {
    lines.push(`${i1}}`);
    lines.push('}');
  }

  return lines.join('\n');
}
